import Avatar from 'ext-ui/components/Avatar/Avatar'
import Pill, { PillProps } from 'ext-ui/components/Pill/Pill'
import React, { ReactElement } from 'react'
import SvgPencilIcon from 'src/components/Svgs/SvgPencilIcon'
import { ExtendCurrency } from 'src/enums'
import { useCurrencyFormatter } from 'src/localization/Crncy'
import { useTranslate } from 'src/localization/Trns'

import { Money, PhysicalCardManagementStatus } from '../cardDetailHeroDemoData'
import SvgLock from '../icons/SvgLock'
import SvgSnowflake from '../icons/SvgSnowflake'
import { useCardDetailHeroData } from '../useCardDetailHeroData'
import { useCardStatusLabel } from '../useCardStatusLabel'
import * as style from './CardSummary.style'

/**
 * The contract carries `cardholder.displayName` and no initials field, so the
 * avatar fallback is derived. Deliberately dumb — first letter of the first two
 * whitespace-separated tokens — rather than trying to be clever about particles,
 * suffixes, or multi-word surnames.
 */
const initialsFrom = (displayName: string): string =>
  displayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()

/**
 * Minor units → major units at the display edge. The scale is currency-aware:
 * most currencies are 2-decimal (÷100), but zero-decimal (JPY) and three-decimal
 * (BHD) currencies are not — a hardcoded ÷100 would misprice them. Falls back to
 * ÷100 for any code Intl can't resolve.
 */
const toDisplayAmount = (money: Money): number => {
  let fractionDigits = 2
  try {
    fractionDigits =
      new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: money.currency,
      }).resolvedOptions().maximumFractionDigits ?? 2
  } catch {
    fractionDigits = 2
  }
  return money.amountMinor / 10 ** fractionDigits
}

/**
 * Colour never carries the status alone — every state also has a distinct
 * label, per the WCAG rule that colour must not be the only means of conveying
 * information.
 *
 * FROZEN and LOCKED are intentionally different: FROZEN is a reversible,
 * user-initiated hold, while LOCKED is issuer-imposed and clears only through
 * the unlock-request capability. The contract is explicit that LOCKED must
 * never be presented as a user freeze.
 */
export const statusPillColor = (
  status: PhysicalCardManagementStatus,
): PillProps['color'] => {
  switch (status) {
    case 'ACTIVE':
      return 'highlight'
    case 'PENDING_ISSUANCE':
      return 'pending'
    case 'FROZEN':
      // Blue ("cold") to match the freeze language in the design — distinct from
      // the amber pending-issuance state and the severe (red) locked state.
      return 'saas'
    case 'LOCKED':
    case 'FAILED':
      return 'severe'
    case 'CANCELED':
      return undefined
  }
}

/**
 * Optional leading glyph in the status pill, where the design uses one: frozen
 * carries a snowflake (the "cold" freeze language) and locked a padlock. Other
 * states are label-only. The glyph inherits the pill's text colour via
 * currentColor.
 */
const statusPillIcon = (
  status: PhysicalCardManagementStatus,
): ReactElement | null => {
  switch (status) {
    case 'FROZEN':
      return <SvgSnowflake width={11} height={11} />
    case 'LOCKED':
      return <SvgLock width={11} height={11} />
    default:
      return null
  }
}

type CardSummaryProps = {
  /**
   * Card status is owned by the hero shell — so the pill, the card-art overlay,
   * and the freeze button never diverge — and passed in, rather than read from
   * the demo data here.
   */
  status: PhysicalCardManagementStatus
}

const CardSummary: React.FC<CardSummaryProps> = ({ status }): ReactElement => {
  const translate = useTranslate()
  const currencyFormatter = useCurrencyFormatter()
  const statusLabel = useCardStatusLabel()
  const { card, limits } = useCardDetailHeroData()

  const formattedAvailable = currencyFormatter({
    amount: toDisplayAmount(limits.cardFinancials.availableSpend),
    currency: limits.cardFinancials.availableSpend.currency as ExtendCurrency,
    maximumFractionDigits: 0,
  })
  const formattedLimit = currencyFormatter({
    amount: toDisplayAmount(limits.permanentCardSpendControl.limit),
    currency: limits.permanentCardSpendControl.limit.currency as ExtendCurrency,
    maximumFractionDigits: 0,
  })

  return (
    <div css={style.wrapper}>
      <div>
        <div css={style.identityRow}>
          <Avatar
            size={28}
            fallback={
              <span className="fallback-text">
                {initialsFrom(card.cardholder.displayName)}
              </span>
            }
          />
          <h2 css={style.cardholderName} data-testid="cardholder-name">
            {card.cardholder.displayName}
          </h2>
        </div>
        <div css={style.metaRow}>
          <span css={style.metaText}>
            <button
              type="button"
              // WCAG 2.5.3 (Label in Name): the accessible name must contain the
              // visible label, or speech-input users cannot target the control by
              // the only text they can see.
              aria-label={translate('${nickname}, edit card nickname', {
                nickname: card.nickname,
              })}
              css={style.nicknameTrigger}
            >
              {card.nickname}
              <SvgPencilIcon
                width={20}
                height={20}
                fill="currentColor"
                stroke="currentColor"
              />
            </button>
            {' · '}
            {translate('Physical')}
            {` · •• ${card.last4}`}
          </span>
          <Pill color={statusPillColor(status)} size="small">
            <span css={style.pillContent} data-testid="status-pill-content">
              {statusPillIcon(status)}
              {statusLabel(status)}
            </span>
          </Pill>
        </div>
      </div>
      <div css={style.creditBlock}>
        <span css={style.creditLabel}>{translate('Available credit')}</span>
        <span css={style.creditAmount}>{formattedAvailable}</span>
        <span css={style.creditLimit}>
          {translate('of ${limit} limit', { limit: formattedLimit })}
        </span>
      </div>
    </div>
  )
}

export default CardSummary
